import { indexedDBUploadManager } from "./indexedDBUploadManager";
import { offlineModeManager } from "./offlineModeManager";

// Constants
const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAYS = [1000, 2000, 5000, 10000, 30000];
const DEFAULT_WAIT_TIMEOUT = 60000; // 1 minute
const CLEANUP_INTERVAL = 10 * 60 * 1000; // 10 minutes
const CLEANUP_MAX_AGE = 30 * 60 * 1000; // 30 minutes
const RETRY_CHECK_INTERVAL = 30 * 1000; // 30 seconds
const POLL_INTERVAL = 500; // 500ms
const COMPLETED_CLEANUP_DELAY = 5 * 60 * 1000; // 5 minutes
const DRAFT_EVENTS_RETRY_BASE_DELAY = 2000; // 2 seconds base delay for exponential backoff

class TextAnswerRetryManager {
  constructor() {
    this.activeRetries = new Map(); // questionId -> retry info
    this.failedAnswers = new Map(); // questionId -> failed answer data
    this.completedAnswers = new Map(); // questionId -> completion info
    this.cleanupCallbacks = new Map(); // questionId -> cleanup callbacks
    this.maxCompletedRetention = 5; // Keep last 5 completed answers for debugging
    this.cleanupInterval = null;
    this.retryInterval = null; // Periodic retry check interval
    this.resumePromise = null; // ✅ NEW: Lock for resume operations

    // Start periodic cleanup and retry checks
    this.startPeriodicCleanup();
    this.startPeriodicRetry();
  }

  // Start background retry for a text answer
  async startBackgroundRetry(questionId, answerData, apiCallFn) {
    // ✅ CRITICAL FIX: Skip video/audio questions - handled by uploadRetryManager
    const questionType = answerData.questionType || answerData.payload?.type;
    if (questionType === "video" || questionType === "audio") {
      console.log(
        `⚠️ [Text Retry Manager] Skipping ${questionType} question ${questionId} - handled by uploadRetryManager`
      );
      return;
    }

    if (this.activeRetries.has(questionId)) {
      return;
    }

    // ✅ CRITICAL FIX: Set activeRetries SYNCHRONOUSLY before any await
    const retryInfo = {
      questionId,
      startTime: Date.now(),
      status: "retrying",
      answerData,
      apiCallFn,
      attempts: 0,
      maxAttempts: MAX_RETRY_ATTEMPTS,
      lastError: null,
    };

    this.activeRetries.set(questionId, retryInfo);

    // ✅ Store complete data in IndexedDB for persistence
    try {
      await indexedDBUploadManager.storePendingAnswer(questionId, answerData);
    } catch (error) {
      console.error(
        `❌ [Text Retry Manager] Error storing answer in IndexedDB for question ${questionId}:`,
        error
      );
      // Continue with in-memory storage if IndexedDB fails
    }

    // Start background retry
    this.executeBackgroundRetry(questionId);
  }

  // Execute background retry with exponential backoff
  async executeBackgroundRetry(questionId) {
    const retryInfo = this.activeRetries.get(questionId);
    if (!retryInfo) {
      // ✅ Try to restore from IndexedDB if not in memory
      try {
        const indexedData =
          await indexedDBUploadManager.getPendingAnswer(questionId);
        // ✅ FIX: Include "retrying" status to resume interrupted retries
        if (
          indexedData &&
          (indexedData.status === "pending" ||
            indexedData.status === "failed" ||
            indexedData.status === "retrying")
        ) {
          // Restore to active retries
          const { updateCandidateResult } =
            await import("../../services/assessment.api");
          this.activeRetries.set(questionId, {
            questionId,
            startTime: Date.now(),
            status: "retrying",
            answerData: indexedData,
            apiCallFn: (assessmentId, payload) =>
              updateCandidateResult(assessmentId, payload),
            attempts: indexedData.attempts || 0,
            maxAttempts: indexedData.maxAttempts || MAX_RETRY_ATTEMPTS,
            lastError: indexedData.error || null,
          });
          // Continue with retry
          return this.executeBackgroundRetry(questionId);
        }
      } catch (error) {
        console.error(
          `❌ [Text Retry Manager] Error restoring from IndexedDB for question ${questionId}:`,
          error
        );
      }
      return;
    }

    // Check if max attempts reached
    if (retryInfo.attempts >= retryInfo.maxAttempts) {
      console.log(
        `❌ [Text Retry Manager] Max attempts reached for question ${questionId}`
      );
      await indexedDBUploadManager.updateUploadStatus(
        `ANSWER_${questionId}`,
        "failed",
        `Max retry attempts (${retryInfo.maxAttempts}) exceeded`
      );
      this.activeRetries.delete(questionId);
      this.failedAnswers.set(questionId, {
        questionId,
        answerData: retryInfo.answerData,
        error: retryInfo.lastError || "Max retry attempts exceeded",
        attempts: retryInfo.attempts,
        timestamp: Date.now(),
      });
      this.triggerFailureCallback(questionId, {
        error: retryInfo.lastError || "Max retry attempts exceeded",
        attempts: retryInfo.attempts,
      });
      return;
    }

    // Calculate exponential backoff delay
    const delays = RETRY_DELAYS;
    const delay =
      delays[Math.min(retryInfo.attempts, delays.length - 1)] ||
      delays[delays.length - 1];

    // Wait before retry (except for first attempt)
    if (retryInfo.attempts > 0) {
      console.log(
        `⏳ [Text Retry Manager] Waiting ${delay}ms before retry attempt ${
          retryInfo.attempts + 1
        } for question ${questionId}`
      );
      await new Promise((resolve) => setTimeout(resolve, delay));
    }

    try {
      // Update status to retrying
      retryInfo.attempts++;
      retryInfo.status = "retrying";
      await indexedDBUploadManager.updateUploadStatus(
        `ANSWER_${questionId}`,
        "retrying",
        null
      );

      // ✅ NEW: For programming questions, ensure draft and events are saved first
      const answerData = retryInfo.answerData;
      const { candidateAssessmentId, payload } = answerData;
      const questionType = answerData.questionType || payload?.type;

      if (questionType === "programming") {
        const programmingLanguageId =
          payload?.programmingLanguageId || payload?.programmingLanguageId;

        if (programmingLanguageId) {
          // Step 1: Save draft if pending
          try {
            const pendingDraft = await indexedDBUploadManager.getPendingDraft(
              questionId,
              programmingLanguageId
            );

            if (pendingDraft && pendingDraft.status === "pending") {
              console.log(
                `📝 [Text Retry Manager] Saving pending draft for programming question ${questionId}`
              );
              const { saveDraft } =
                await import("../../services/assessment.api");

              try {
                await saveDraft(pendingDraft.assessmentId, {
                  candidateAssessmentId: pendingDraft.candidateAssessmentId,
                  skill: pendingDraft.skill,
                  questionId: pendingDraft.questionId,
                  languageId: pendingDraft.languageId,
                  code: pendingDraft.code,
                });

                // Mark draft as completed
                await indexedDBUploadManager.markDraftCompleted(
                  questionId,
                  programmingLanguageId
                );
              } catch (draftError) {
                console.error(
                  `❌ [Text Retry Manager] Failed to save draft for question ${questionId}:`,
                  draftError
                );
                // Continue with submission even if draft save fails
              }
            }
          } catch (draftCheckError) {
            console.warn(
              `⚠️ [Text Retry Manager] Error checking pending draft:`,
              draftCheckError
            );
          }

          // Step 2: Save editor events if pending
          try {
            const pendingEvents =
              await indexedDBUploadManager.getPendingEditorEvents(
                questionId,
                programmingLanguageId
              );

            if (pendingEvents && pendingEvents.status === "pending") {
              const { saveEditorEvents } =
                await import("../../services/assessment.api");

              try {
                await saveEditorEvents({
                  candidateAssessmentId: pendingEvents.candidateAssessmentId,
                  assessmentId: pendingEvents.assessmentId,
                  questionId: pendingEvents.questionId,
                  languageId: pendingEvents.languageId,
                  skill: pendingEvents.skill,
                  events: pendingEvents.events,
                  sessionStartTime: pendingEvents.sessionStartTime,
                  totalEvents: pendingEvents.totalEvents,
                  initialContext: pendingEvents.initialContext,
                });

                // Mark events as completed
                await indexedDBUploadManager.markEditorEventsCompleted(
                  questionId,
                  programmingLanguageId
                );
              } catch (eventsError) {
                console.error(
                  `❌ [Text Retry Manager] Failed to save editor events for question ${questionId}:`,
                  eventsError
                );
                // Continue with submission even if events save fails
              }
            }
          } catch (eventsCheckError) {
            console.warn(
              `⚠️ [Text Retry Manager] Error checking pending events:`,
              eventsCheckError
            );
          }
        }
      }

      // Step 3: Submit the answer
      // Call the API function
      const result = await retryInfo.apiCallFn(candidateAssessmentId, payload);

      // ✅ Check if response was queued (still offline)
      if (result?.data?.queued) {
        await indexedDBUploadManager.updateUploadStatus(
          `ANSWER_${questionId}`,
          "pending",
          "Still offline - waiting for connection"
        );
        this.activeRetries.delete(questionId);
        return; // Exit without throwing - will retry when online
      }

      // ✅ Success - mark as completed
      await indexedDBUploadManager.markUploadCompleted(`ANSWER_${questionId}`);

      // Clean up
      this.activeRetries.delete(questionId);

      // Store completion info
      this.completedAnswers.set(questionId, {
        questionId,
        completedAt: Date.now(),
        attempts: retryInfo.attempts,
        status: "success",
      });

      // Schedule cleanup
      this.scheduleCompletedCleanup(questionId);

      // Trigger success callback
      this.triggerSuccessCallback(questionId, result);
    } catch (error) {
      console.error(
        `❌ [Text Retry Manager] Retry attempt ${retryInfo.attempts} failed for question ${questionId}:`,
        error
      );

      retryInfo.lastError = error.message || "Unknown error";

      // ✅ Check if error is offline-related
      const isOffline =
        !navigator.onLine ||
        error.message?.toLowerCase().includes("network") ||
        error.message?.toLowerCase().includes("failed to fetch") ||
        error.code === "ECONNABORTED" ||
        error.code === "ERR_NETWORK" ||
        error.response === undefined;

      if (isOffline) {
        // Still offline - update status and wait
        await indexedDBUploadManager.updateUploadStatus(
          `ANSWER_${questionId}`,
          "pending",
          "Still offline - waiting for connection"
        );
        this.activeRetries.delete(questionId);
        return; // Exit without throwing - will retry when online
      }

      // Check if max attempts reached
      if (retryInfo.attempts >= retryInfo.maxAttempts) {
        await indexedDBUploadManager.updateUploadStatus(
          `ANSWER_${questionId}`,
          "failed",
          retryInfo.lastError
        );
        this.activeRetries.delete(questionId);
        this.failedAnswers.set(questionId, {
          questionId,
          answerData: retryInfo.answerData,
          error: retryInfo.lastError,
          attempts: retryInfo.attempts,
          timestamp: Date.now(),
        });
        this.triggerFailureCallback(questionId, {
          error: retryInfo.lastError,
          attempts: retryInfo.attempts,
        });
      } else {
        // Schedule next retry
        setTimeout(() => {
          this.executeBackgroundRetry(questionId);
        }, delay);
      }
    }
  }

  // Manual retry from UI
  async manualRetry(questionId, apiCallFn) {
    let failedData = this.getFailedAnswer(questionId);

    // ✅ Try to restore from IndexedDB if not in memory
    if (!failedData) {
      try {
        const indexedData =
          await indexedDBUploadManager.getPendingAnswer(questionId);
        if (indexedData) {
          failedData = {
            questionId,
            answerData: indexedData,
            error: indexedData.error,
            attempts: indexedData.attempts,
            timestamp: indexedData.timestamp,
          };
        }
      } catch (error) {
        console.error(
          `❌ [Text Retry Manager] Error restoring from IndexedDB for manual retry:`,
          error
        );
      }
    }

    if (!failedData) {
      throw new Error("No failed answer data found for manual retry");
    }

    // Reset attempts for manual retry
    const { candidateAssessmentId, payload } = failedData.answerData;

    try {
      console.log(
        `🔄 [Text Retry Manager] Manual retry for question ${questionId}`
      );

      // Update status
      await indexedDBUploadManager.updateUploadStatus(
        `ANSWER_${questionId}`,
        "retrying",
        null
      );

      // Call API
      const result = await apiCallFn(candidateAssessmentId, payload);

      // ✅ Check if response was queued (still offline)
      if (result?.data?.queued) {
        await indexedDBUploadManager.updateUploadStatus(
          `ANSWER_${questionId}`,
          "pending",
          "Still offline - waiting for connection"
        );
        throw new Error("Still offline - answer queued");
      }

      // ✅ Success
      await indexedDBUploadManager.markUploadCompleted(`ANSWER_${questionId}`);
      this.clearFailedAnswer(questionId);

      this.completedAnswers.set(questionId, {
        questionId,
        completedAt: Date.now(),
        attempts: 1,
        status: "manual_retry_success",
      });

      this.scheduleCompletedCleanup(questionId);

      return { success: true, result };
    } catch (error) {
      console.error(
        `❌ [Text Retry Manager] Manual retry failed for question ${questionId}:`,
        error
      );

      await indexedDBUploadManager.updateUploadStatus(
        `ANSWER_${questionId}`,
        "failed",
        error.message
      );

      throw error;
    }
  }

  // Check if retry is active
  isRetrying(questionId) {
    return this.activeRetries.has(questionId);
  }

  // Get failed answer data
  getFailedAnswer(questionId) {
    return this.failedAnswers.get(questionId);
  }

  // Clear failed answer data
  clearFailedAnswer(questionId) {
    this.failedAnswers.delete(questionId);
  }

  // Get retry status
  getRetryStatus(questionId) {
    if (this.activeRetries.has(questionId)) {
      const retryInfo = this.activeRetries.get(questionId);
      return {
        status: "retrying",
        attempts: retryInfo.attempts,
        maxAttempts: retryInfo.maxAttempts,
        lastError: retryInfo.lastError,
      };
    }

    if (this.failedAnswers.has(questionId)) {
      const failed = this.failedAnswers.get(questionId);
      return {
        status: "failed",
        attempts: failed.attempts,
        error: failed.error,
        timestamp: failed.timestamp,
      };
    }

    return null;
  }

  // Schedule cleanup of completed answer
  scheduleCompletedCleanup(questionId) {
    if (window.requestIdleCallback) {
      window.requestIdleCallback(
        () => {
          this.cleanupCompletedAnswer(questionId);
        },
        { timeout: COMPLETED_CLEANUP_DELAY }
      );
    } else {
      setTimeout(() => {
        this.cleanupCompletedAnswer(questionId);
      }, COMPLETED_CLEANUP_DELAY);
    }
  }

  // Clean up completed answer
  cleanupCompletedAnswer(questionId) {
    if (this.completedAnswers.has(questionId)) {
      this.completedAnswers.delete(questionId);
    }
  }

  // Start periodic cleanup
  startPeriodicCleanup() {
    this.cleanupInterval = setInterval(() => {
      this.performPeriodicCleanup();
    }, CLEANUP_INTERVAL);
  }

  // Perform periodic cleanup
  performPeriodicCleanup() {
    const now = Date.now();
    const maxAge = CLEANUP_MAX_AGE;

    // Clean up old completed answers
    for (const [questionId, completed] of this.completedAnswers.entries()) {
      if (now - completed.completedAt > maxAge) {
        this.completedAnswers.delete(questionId);
      }
    }

    // Clean up old failed answers
    for (const [questionId, failed] of this.failedAnswers.entries()) {
      if (now - failed.timestamp > maxAge) {
        this.failedAnswers.delete(questionId);
      }
    }

    // Clean up old active retries (stuck retries)
    for (const [questionId, retry] of this.activeRetries.entries()) {
      if (now - retry.startTime > maxAge) {
        this.activeRetries.delete(questionId);
      }
    }
  }

  // Start periodic retry checks
  startPeriodicRetry() {
    this.retryInterval = setInterval(() => {
      this.checkAndRetryPending();
    }, RETRY_CHECK_INTERVAL);
  }

  // Check and retry pending answers
  async checkAndRetryPending() {
    if (!navigator.onLine) {
      return; // Skip if offline
    }

    if (!offlineModeManager.isOfflineModeSupported()) {
      return; // Skip if IndexedDB not available
    }

    try {
      const pendingAnswers =
        await indexedDBUploadManager.getAllPendingAnswers();

      for (const answer of pendingAnswers) {
        // ✅ CRITICAL FIX: Skip video/audio questions - handled by uploadRetryManager
        const questionType = answer.questionType;
        if (questionType === "video" || questionType === "audio") {
          continue; // Skip video/audio questions
        }

        // ✅ NEW: Handle DRAFT/EVENTS records independently - they have all required data
        if (answer.isDraft || answer.isEditorEvents) {
          const questionId = answer.questionId;
          const languageId = answer.languageId;

          if (!languageId) {
            continue;
          }

          // ✅ Process DRAFT/EVENTS independently
          try {
            if (answer.isDraft) {
              const draftData = await indexedDBUploadManager.getPendingDraft(
                questionId,
                languageId
              );
              if (
                draftData &&
                (draftData.status === "pending" ||
                  draftData.status === "failed")
              ) {
                await this.processDraftRetry(questionId, languageId, draftData);
              }
            } else if (answer.isEditorEvents) {
              const eventsData =
                await indexedDBUploadManager.getPendingEditorEvents(
                  questionId,
                  languageId
                );
              if (
                eventsData &&
                (eventsData.status === "pending" ||
                  eventsData.status === "failed")
              ) {
                await this.processEventsRetry(
                  questionId,
                  languageId,
                  eventsData
                );
              }
            }
          } catch (error) {
            console.error(
              `Error processing ${
                answer.isDraft ? "DRAFT" : "EVENTS"
              } for question ${questionId}:`,
              error
            );
          }
          continue;
        }

        // Skip if already retrying
        if (this.isRetrying(answer.questionId)) {
          continue;
        }

        // Skip if max attempts reached
        if (answer.attempts >= (answer.maxAttempts || MAX_RETRY_ATTEMPTS)) {
          continue;
        }

        // Start background retry
        const { updateCandidateResult } =
          await import("../../services/assessment.api");
        const answerData = await indexedDBUploadManager.getPendingAnswer(
          answer.questionId
        );

        if (answerData) {
          this.startBackgroundRetry(
            answer.questionId,
            answerData,
            (assessmentId, payload) =>
              updateCandidateResult(assessmentId, payload)
          );
        }
      }
    } catch (error) {
      console.error(
        "❌ [Text Retry Manager] Error checking pending answers:",
        error
      );
    }
  }

  // Resume pending answers (called on network restore)
  async resumePendingAnswers() {
    if (!offlineModeManager.isOfflineModeSupported()) {
      console.log("IndexedDB unavailable - skipping resume pending answers");
      return 0;
    }

    // ✅ CRITICAL FIX: Prevent concurrent resume operations
    if (this.resumePromise) {
      console.log(
        "⚠️ [Text Retry Manager] Resume operation already in progress - waiting for it to complete"
      );
      return this.resumePromise;
    }

    this.resumePromise = (async () => {
      try {
        const pendingAnswers =
          await indexedDBUploadManager.getAllPendingAnswers();
        let resumedCount = 0;

        // ✅ FIX: Track which questions we've already resumed in this call
        const processedQuestionIds = new Set();

        for (const answer of pendingAnswers) {
          const questionId = answer.questionId;
          const questionType = answer.questionType;

          // ✅ FIX: Skip if already processing this question
          if (processedQuestionIds.has(questionId)) {
            continue;
          }

          // ✅ CRITICAL FIX: Skip video/audio questions - handled by uploadRetryManager
          if (questionType === "video" || questionType === "audio") {
            console.log(
              `⚠️ [Text Retry Manager] Skipping ${questionType} question ${questionId} - handled by uploadRetryManager`
            );
            continue; // Skip video/audio questions
          }

          // ✅ NEW: Handle DRAFT/EVENTS records independently - they have all required data
          if (answer.isDraft || answer.isEditorEvents) {
            const languageId = answer.languageId;

            if (!languageId) {
              continue;
            }

            // ✅ Process DRAFT/EVENTS independently
            try {
              processedQuestionIds.add(questionId);

              if (answer.isDraft) {
                const draftData = await indexedDBUploadManager.getPendingDraft(
                  questionId,
                  languageId
                );
                // ✅ FIX: Include "retrying" status to resume interrupted retries
                if (
                  draftData &&
                  (draftData.status === "pending" ||
                    draftData.status === "failed" ||
                    draftData.status === "retrying")
                ) {
                  await this.processDraftRetry(
                    questionId,
                    languageId,
                    draftData
                  );
                  resumedCount++;
                }
              } else if (answer.isEditorEvents) {
                const eventsData =
                  await indexedDBUploadManager.getPendingEditorEvents(
                    questionId,
                    languageId
                  );
                // ✅ FIX: Include "retrying" status to resume interrupted retries
                if (
                  eventsData &&
                  (eventsData.status === "pending" ||
                    eventsData.status === "failed" ||
                    eventsData.status === "retrying")
                ) {
                  await this.processEventsRetry(
                    questionId,
                    languageId,
                    eventsData
                  );
                  resumedCount++;
                }
              }
            } catch (error) {
              console.error(
                `Error processing ${
                  answer.isDraft ? "DRAFT" : "EVENTS"
                } for question ${questionId}:`,
                error
              );
            }
            continue;
          }

          // ✅ Get full answer data for processing (only for ANSWER records)
          const answerData =
            await indexedDBUploadManager.getPendingAnswer(questionId);

          if (!answerData) {
            continue;
          }

          // ✅ FIX: Skip if already retrying
          if (this.isRetrying(questionId)) {
            console.log(
              `⏭️ [Text Retry Manager] Skipping question ${questionId} - already retrying`
            );
            continue;
          }

          // ✅ FIX: Check max attempts
          const maxAttempts = answerData.maxAttempts || MAX_RETRY_ATTEMPTS;
          const currentAttempts = answerData.attempts || 0;

          if (currentAttempts >= maxAttempts) {
            continue;
          }

          // ✅ FIX: Include "retrying" status to resume interrupted retries
          if (
            answer.status === "pending" ||
            answer.status === "failed" ||
            answer.status === "retrying"
          ) {
            try {
              // ✅ FIX: Mark as processed before starting retry
              processedQuestionIds.add(questionId);

              const { updateCandidateResult } =
                await import("../../services/assessment.api");

              // ✅ CRITICAL FIX: Don't set activeRetries here - let startBackgroundRetry handle it
              // Setting it here causes startBackgroundRetry to return early (line 42 check)
              // which prevents executeBackgroundRetry from being called
              this.startBackgroundRetry(
                questionId,
                answerData,
                (assessmentId, payload) =>
                  updateCandidateResult(assessmentId, payload)
              );
              resumedCount++;
            } catch (error) {
              console.error(
                `Error resuming answer for question ${questionId}:`,
                error
              );
              // ✅ FIX: Remove from activeRetries on error to allow retry
              this.activeRetries.delete(questionId);
            }
          }
        }

        return resumedCount;
      } catch (error) {
        console.error("Error resuming pending answers:", error);
        return 0;
      } finally {
        this.resumePromise = null;
      }
    })();

    return this.resumePromise;
  }

  // ✅ NEW: Get all active retry question IDs
  getActiveRetryQuestionIds() {
    return Array.from(this.activeRetries.keys());
  }

  // Helper: Wait for condition with timeout and polling
  async waitForCondition(
    conditionFn,
    maxWaitTime,
    pollInterval = POLL_INTERVAL
  ) {
    const startTime = Date.now();

    while (!conditionFn()) {
      const elapsed = Date.now() - startTime;

      if (elapsed >= maxWaitTime) {
        return {
          success: false,
          timeout: true,
        };
      }

      await new Promise((resolve) => setTimeout(resolve, pollInterval));
    }

    return {
      success: true,
      timeout: false,
    };
  }

  // ✅ NEW: Wait for all active retries to complete (with timeout)
  async waitForAllActiveRetries(maxWaitTime = DEFAULT_WAIT_TIMEOUT) {
    const result = await this.waitForCondition(
      () => this.activeRetries.size === 0,
      maxWaitTime
    );

    if (!result.success) {
      console.warn(
        `⚠️ [Text Retry Manager] Timeout waiting for ${this.activeRetries.size} active retries`
      );
      return {
        ...result,
        pendingCount: this.activeRetries.size,
        pendingQuestionIds: this.getActiveRetryQuestionIds(),
      };
    }

    return {
      ...result,
      pendingCount: 0,
    };
  }

  // Get pending answers count
  async getPendingAnswersCount() {
    const memoryCount = {
      activeRetries: this.activeRetries.size,
      failedAnswers: this.failedAnswers.size,
      total: this.activeRetries.size + this.failedAnswers.size,
    };

    if (!offlineModeManager.isOfflineModeSupported()) {
      return memoryCount;
    }

    try {
      const indexedDBCount =
        await indexedDBUploadManager.getAllPendingAnswers();
      return {
        activeRetries: memoryCount.activeRetries,
        failedAnswers: memoryCount.failedAnswers,
        indexedDB: indexedDBCount.length,
        total: memoryCount.total + indexedDBCount.length,
      };
    } catch (error) {
      console.error("Error getting IndexedDB pending count:", error);
      return memoryCount;
    }
  }

  // Cleanup for a specific question
  cleanupQuestion(questionId) {
    if (this.activeRetries.has(questionId)) {
      this.activeRetries.delete(questionId);
    }

    if (this.failedAnswers.has(questionId)) {
      this.failedAnswers.delete(questionId);
    }

    if (this.completedAnswers.has(questionId)) {
      this.completedAnswers.delete(questionId);
    }

    if (this.cleanupCallbacks.has(questionId)) {
      this.cleanupCallbacks.delete(questionId);
    }
  }

  // Force cleanup all data
  forceCleanup() {
    this.activeRetries.clear();
    this.failedAnswers.clear();
    this.completedAnswers.clear();
    this.cleanupCallbacks.clear();

    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }

    if (this.retryInterval) {
      clearInterval(this.retryInterval);
      this.retryInterval = null;
    }
  }

  // Register cleanup callback
  registerCleanupCallback(questionId, callback) {
    this.cleanupCallbacks.set(questionId, callback);
  }

  // Trigger cleanup callback
  triggerCleanupCallback(questionId) {
    const callback = this.cleanupCallbacks.get(questionId);
    if (callback && typeof callback === "function") {
      try {
        callback();
      } catch (error) {
        console.error(
          `Error in cleanup callback for question ${questionId}:`,
          error
        );
      }
    }
  }

  // Trigger success callback
  triggerSuccessCallback(questionId, result) {}

  // Trigger failure callback
  triggerFailureCallback(questionId, result) {}

  // ✅ NEW: Process draft retry independently
  async processDraftRetry(questionId, languageId, draftData) {
    // Check max attempts
    const maxAttempts = draftData.maxAttempts || MAX_RETRY_ATTEMPTS;
    const currentAttempts = draftData.attempts || 0;

    if (currentAttempts >= maxAttempts) {
      console.log(
        `⏭️ [Text Retry Manager] Skipping DRAFT for question ${questionId} - max attempts (${maxAttempts}) reached`
      );
      return;
    }

    // Update status
    await indexedDBUploadManager.updateUploadStatus(
      `DRAFT_${questionId}_${languageId}`,
      "retrying",
      null
    );

    try {
      const { saveDraft } =
        await import("../../services/assessment.api");
      await saveDraft(draftData.assessmentId, {
        candidateAssessmentId: draftData.candidateAssessmentId,
        skill: draftData.skill,
        questionId: draftData.questionId,
        languageId: draftData.languageId,
        code: draftData.code,
      });

      // Mark as completed
      await indexedDBUploadManager.markDraftCompleted(questionId, languageId);
      console.log(
        `✅ [Text Retry Manager] Draft saved successfully for question ${questionId}`
      );
    } catch (error) {
      console.error(
        `❌ [Text Retry Manager] Failed to save draft for question ${questionId}:`,
        error
      );

      // Get updated attempts (updateUploadStatus already incremented it)
      const updatedDraft = await indexedDBUploadManager.getPendingDraft(
        questionId,
        languageId
      );
      const updatedAttempts = updatedDraft?.attempts || currentAttempts + 1;

      // Update status and retry if not max attempts
      if (updatedAttempts < maxAttempts) {
        await indexedDBUploadManager.updateUploadStatus(
          `DRAFT_${questionId}_${languageId}`,
          "pending",
          error.message
        );
        // Retry after delay
        setTimeout(() => {
          this.processDraftRetry(questionId, languageId, {
            ...draftData,
            attempts: updatedAttempts,
          });
        }, DRAFT_EVENTS_RETRY_BASE_DELAY * updatedAttempts); // Exponential backoff
      } else {
        await indexedDBUploadManager.updateUploadStatus(
          `DRAFT_${questionId}_${languageId}`,
          "failed",
          error.message
        );
      }
    }
  }

  // ✅ NEW: Process events retry independently
  async processEventsRetry(questionId, languageId, eventsData) {
    // Check max attempts
    const maxAttempts = eventsData.maxAttempts || MAX_RETRY_ATTEMPTS;
    const currentAttempts = eventsData.attempts || 0;

    if (currentAttempts >= maxAttempts) {
      return;
    }

    // Update status
    await indexedDBUploadManager.updateUploadStatus(
      `EVENTS_${questionId}_${languageId}`,
      "retrying",
      null
    );

    try {
      const { saveEditorEvents } =
        await import("../../services/assessment.api");
      await saveEditorEvents({
        candidateAssessmentId: eventsData.candidateAssessmentId,
        assessmentId: eventsData.assessmentId,
        questionId: eventsData.questionId,
        languageId: eventsData.languageId,
        skill: eventsData.skill,
        events: eventsData.events,
        sessionStartTime: eventsData.sessionStartTime,
        totalEvents: eventsData.totalEvents,
        initialContext: eventsData.initialContext,
      });

      // Mark as completed
      await indexedDBUploadManager.markEditorEventsCompleted(
        questionId,
        languageId
      );
      console.log(
        `✅ [Text Retry Manager] Editor events saved successfully for question ${questionId}`
      );
    } catch (error) {
      console.error(
        `❌ [Text Retry Manager] Failed to save editor events for question ${questionId}:`,
        error
      );

      // Get updated attempts (updateUploadStatus already incremented it)
      const updatedEvents = await indexedDBUploadManager.getPendingEditorEvents(
        questionId,
        languageId
      );
      const updatedAttempts = updatedEvents?.attempts || currentAttempts + 1;

      // Update status and retry if not max attempts
      if (updatedAttempts < maxAttempts) {
        await indexedDBUploadManager.updateUploadStatus(
          `EVENTS_${questionId}_${languageId}`,
          "pending",
          error.message
        );
        // Retry after delay
        setTimeout(() => {
          this.processEventsRetry(questionId, languageId, {
            ...eventsData,
            attempts: updatedAttempts,
          });
        }, DRAFT_EVENTS_RETRY_BASE_DELAY * updatedAttempts); // Exponential backoff
      } else {
        await indexedDBUploadManager.updateUploadStatus(
          `EVENTS_${questionId}_${languageId}`,
          "failed",
          error.message
        );
      }
    }
  }
}

// Global instance
export const textAnswerRetryManager = new TextAnswerRetryManager();

// ✅ ENHANCED: Global cleanup on page unload
if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", () => {
    textAnswerRetryManager.forceCleanup();
  });
}

